// @flow
import WebComponent from 'fg-frontend/src/webComponents/WebComponent';

/**
 * Kalkulačka pro výpočet financování vozidel s balónem
 * Automaticky se přepočítává při změnách vstupních hodnot v hidden input polích
 * @extends WebComponent
 */
class CalculatorEasy extends WebComponent {
	/**
	 * @param {Object} props - Vlastnosti komponenty
	 */
	constructor(props) {
		super(props);

		/**
		 * Tabulka balónových procent podle věku vozidla a doby financování
		 * @type {Object.<number, Object.<number, number>>}
		 * @description Klíč = věk vozidla (12, 24, 36, 48 měsíců), hodnota = objekt s dobou financování jako klíčem
		 */
		this.balonTable = {
			12: {
				24: 40,
				36: 35,
				48: 30,
			},
			24: {
				24: 35,
				36: 30,
				48: 25,
			},
			36: {
				24: 30,
				36: 25,
				48: 20,
			},
			48: {
				24: 25,
				36: 20,
				48: 15,
			},
		};

		/**
		 * Seznam ID elementů pro vstupní hodnoty kalkulačky
		 * @type {string[]}
		 */
		this.inputElements = [
			'carPriceEasy',
			'advancePaymentEasy',
			'interestRateEasy',
			'commissioningEasy',
			'financingPeriodEasy',
			'balonEurEasy'
		];

		/**
		 * Pole MutationObserverů a intervalů pro sledování změn
		 * @type {Array<{observer: MutationObserver, intervalId: number}>}
		 */
		this.mutationObservers = [];

		/**
		 * Cache posledních hodnot pro optimalizaci přepočtu
		 * @type {Object.<string, string>}
		 */
		this.lastValues = {};

		this.initEventListeners();
	}

	/**
	 * Lifecycle metoda - spustí se po připojení komponenty do DOM
	 * Inicializuje výpočet a sledování změn
	 */
	didMount() {
		this.calculate();
		this.initMutationObservers();
	}

	/**
	 * Inicializuje event listenery pro input elementy
	 * Nastavuje sledování 'input' a 'change' událostí
	 * @private
	 */
	initEventListeners() {
		this.inputElements.forEach(id => {
			const element = document.getElementById(id);
			if (element) {
				this[id] = element;
				this[id].addEventListener('input', () => this.calculate());
				this[id].addEventListener('change', () => this.calculate());
				this.lastValues[id] = element.value;
			}
		});
	}

	/**
	 * Inicializuje MutationObserver pro sledování změn v hidden input polích
	 * Kombinuje sledování atributů s periodickou kontrolou pro zachycení všech změn
	 * @private
	 */
	initMutationObservers() {
		this.inputElements.forEach(id => {
			const element = document.getElementById(id);
			if (element) {
				const observer = new MutationObserver((mutations) => {
					mutations.forEach((mutation) => {
						if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
							const currentValue = element.value;
							if (this.lastValues[id] !== currentValue) {
								this.lastValues[id] = currentValue;
								this.calculate();
							}
						}
					});
				});

				observer.observe(element, {
					attributes: true,
					attributeFilter: ['value']
				});

				// Periodická kontrola pro případ změny pouze property value
				const intervalId = setInterval(() => {
					const currentValue = element.value;
					if (this.lastValues[id] !== currentValue) {
						this.lastValues[id] = currentValue;
						this.calculate();
					}
				}, 100);

				this.mutationObservers.push({observer, intervalId});
			}
		});
	}

	/**
	 * Hlavní výpočetní funkce kalkulačky
	 * Vypočítává měsíční splátku, balón a celkový úrok na základě vstupních parametrů
	 * @public
	 */
	calculate() {
		try {
			const carPrice = this.getValue('carPriceEasy');
			const advancePayment = this.getValue('advancePaymentEasy');
			const interestRate = this.getValue('interestRateEasy');
			const commissioning = this.getValue('commissioningEasy');
			const financingPeriod = this.getValue('financingPeriodEasy');
			const balonEur = this.getValue('balonEurEasy');

			if (carPrice <= 0 || financingPeriod <= 0) {
				this.setOutputValues({
					monthlyPaymentEasy: 'N/A',
					balonPaymentEasy: 'N/A',
					totalInterestEasy: 'N/A',
					balonPercentageEasy: 'N/A',
					advancePaymentValueEasy: 'N/A'
				});
				return;
			}

			const advancePaymentValue = carPrice * (advancePayment / 100);
			const amountFinancing = carPrice - advancePaymentValue;
			const vehicleAge = this.calculateVehicleAge(commissioning);

			let balonPayment;
			let balonPercentage;

			if (balonEur > 0) {
				balonPayment = balonEur;
				balonPercentage = `${((balonEur / carPrice) * 100).toFixed(1)}%`;
			} else {
				// Balón se počítá z ceny vozu podle tabulky
				const balonValue = this.getBalonValue(vehicleAge, financingPeriod);
				balonPayment = carPrice * (balonValue / 100);
				balonPercentage = `${balonValue}%`;
			}

			const monthlyInterest = interestRate / 100 / 12;
			const pvBalon = balonPayment / Math.pow(1 + monthlyInterest, financingPeriod);
			const annuityRepayment = amountFinancing - pvBalon;

			let monthlyPayment;
			if (monthlyInterest === 0) {
				monthlyPayment = annuityRepayment / financingPeriod;
			} else {
				const numerator = monthlyInterest * Math.pow(1 + monthlyInterest, financingPeriod);
				const denominator = Math.pow(1 + monthlyInterest, financingPeriod) - 1;
				monthlyPayment = annuityRepayment * (numerator / denominator);
			}

			const paidInFull = monthlyPayment * financingPeriod + balonPayment;
			const totalInterest = paidInFull - amountFinancing;

			this.setOutputValues({
				monthlyPaymentEasy: monthlyPayment,
				balonPaymentEasy: balonPayment,
				totalInterestEasy: totalInterest,
				balonPercentageEasy: balonPercentage,
				advancePaymentValueEasy: advancePaymentValue
			});

		} catch (error) {
			console.error('Chyba při výpočtu:', error);
			this.setOutputValues({
				monthlyPaymentEasy: 'Err',
				balonPaymentEasy: 'Err',
				totalInterestEasy: 'Err',
				balonPercentageEasy: 'Err',
				advancePaymentValueEasy: 'Err'
			});
		}
	}

	/**
	 * Vypočítá věk vozidla v měsících od data uvedení do provozu
	 * @param {string} commissioningDate - Datum ve formátu "dd.mm.yyyy"
	 * @returns {number} Věk vozidla v měsících (minimálně 1)
	 * @private
	 */
	calculateVehicleAge(commissioningDate) {
		if (!commissioningDate) return 0;
		const [day, month, year] = commissioningDate.split('.').map(Number);
		const commissioning = new Date(year, month - 1, day);
		const now = new Date();

		let totalMonths = (now.getFullYear() - commissioning.getFullYear()) * 12 +
				(now.getMonth() - commissioning.getMonth());

		if (now.getDate() < commissioning.getDate()) {
			totalMonths--;
		}

		if (totalMonths < 0) return 1;
		return Math.round(totalMonths);
	}

	/**
	 * Získá balónové procento podle věku vozidla a doby financování z tabulky
	 * @param {number} vehicleAge - Věk vozidla v měsících
	 * @param {number} financingPeriod - Doba financování v měsících
	 * @returns {number} Balónové procento (fallback 20% pokud kombinace neexistuje)
	 * @private
	 */
	getBalonValue(vehicleAge, financingPeriod) {
		let financingPeriodCategory;
		if (vehicleAge <= 12) financingPeriodCategory = 12;
		else if (vehicleAge <= 24) financingPeriodCategory = 24;
		else if (vehicleAge <= 36) financingPeriodCategory = 36;
		else financingPeriodCategory = 48;

		if (this.balonTable[financingPeriodCategory] && this.balonTable[financingPeriodCategory][financingPeriod]) {
			return this.balonTable[financingPeriodCategory][financingPeriod];
		}

		console.warn(`Neplatná kombinace: věk ${vehicleAge}M, doba ${financingPeriod}M. Použito 20% balón.`);
		return 20;
	}

	/**
	 * Nastaví výstupní hodnoty do odpovídajících elementů
	 * @param {Object.<string, number|string>} outputValues - Objekt s ID elementu jako klíčem a hodnotou
	 * @private
	 */
	setOutputValues(outputValues) {
		Object.entries(outputValues).forEach(([id, value]) => {
			this.setValue(id, value);
		});
	}

	/**
	 * Získá a zpracuje hodnotu z hidden input pole
	 * Automaticky převádí čísla s čárkou na tečku, pro datum vrací string
	 * @param {string} id - ID elementu
	 * @returns {number|string} Číselná hodnota pro výpočty nebo string pro datum
	 * @private
	 */
	getValue(id) {
		const element = document.getElementById(id);
		if (!element) {
			console.error(`Element s ID "${id}" nebyl nalezen.`);
			return 0;
		}

		const value = element.value || '';

		if (id === 'commissioningEasy') {
			return value;
		}

		// Nahradí čárku tečkou pro správné parsování desetinných čísel
		const normalizedValue = value.replace(',', '.');
		const numValue = parseFloat(normalizedValue || '0');

		return isNaN(numValue) ? 0 : numValue;
	}

	/**
	 * Formátuje číslo jako měnu s mezerami jako oddělovačem tisíců
	 * @param {number} value - Číselná hodnota k zformátování
	 * @returns {string} Zformátované číslo (např. "1 234 567")
	 * @private
	 */
	formatCurrency(value) {
		if (typeof value !== 'number' || isNaN(value)) {
			return '0';
		}

		const rounded = Math.round(value);
		return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	}

	/**
	 * Nastaví hodnotu do output elementu (textContent i value)
	 * @param {string} id - ID cílového elementu
	 * @param {number|string} value - Hodnota k nastavení
	 * @private
	 */
	setValue(id, value) {
		const element = document.getElementById(id);
		if (element) {
			if (typeof value === 'number') {
				const formattedValue = this.formatCurrency(value);
				element.textContent = formattedValue;
				element.value = formattedValue;
			} else {
				element.textContent = value;
				element.value = value;
			}
		}
	}

	/**
	 * Manuální spuštění přepočtu kalkulačky
	 * Užitečné pro externí volání z jiných částí aplikace
	 * @public
	 */
	recalculate() {
		this.calculate();
	}

	/**
	 * Nastaví balón přímo v EUR (přepíše automatický výpočet z tabulky)
	 * @param {number} balonEur - Částka balónu v EUR
	 * @public
	 */
	setBalonEur(balonEur) {
		const element = document.getElementById('balonEurEasy');
		if (element) {
			element.value = balonEur;
			this.lastValues['balonEurEasy'] = balonEur;
			this.calculate();
		}
	}

	/**
	 * Vymaže přímý balón a vrátí se k automatickému výpočtu z tabulky
	 * @public
	 */
	clearBalonEur() {
		const element = document.getElementById('balonEurEasy');
		if (element) {
			element.value = '';
			this.lastValues['balonEurEasy'] = '';
			this.calculate();
		}
	}

	/**
	 * Lifecycle metoda - vyčistí zdroje před odstraněním komponenty
	 * Odpojuje MutationObservery a ruší intervaly
	 */
	willUnMount() {
		this.mutationObservers.forEach(({observer, intervalId}) => {
			observer.disconnect();
			clearInterval(intervalId);
		});
		this.mutationObservers = [];
	}
}

export default CalculatorEasy;



// WEBPACK FOOTER //
// ./fe/web-default/src/js/components/CalculatorEasy.js